import {AsyncPipe, NgClass} from "@angular/common"
import {ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal} from "@angular/core"
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"
import {ConfiguratorVariant} from "@cm/pricing"
import {ConfigInfo, unwrapInterfaceId, VariantInfo} from "@cm/template-nodes"
import {ConfigMenuService} from "@common/components/viewers/configurator/config-menu/services/config-menu.service"
import {cmTooltipAppendTo} from "@common/config/tippy"
import {TippyDirective} from "@ngneat/helipopper"
import {map, Observable, of, switchMap} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-config-button",
    templateUrl: "./config-button.component.html",
    styleUrls: ["./config-button.component.scss"],
    imports: [TippyDirective, AsyncPipe, NgClass, LoadingSpinnerIconComponent],
})
export class ConfigButtonComponent {
    readonly $asLabel = input.required<boolean>({alias: "asLabel"})
    readonly $tooltipContainer = input<HTMLDivElement | undefined>(undefined, {alias: "tooltipContainer"})
    readonly $config = input.required<ConfigInfo | undefined>({alias: "config"})
    readonly $variant = input.required<VariantInfo | undefined>({alias: "variant"})
    readonly $selectedVariant = input<VariantInfo | undefined>(undefined, {alias: "selectedVariant"})
    readonly $notAvailableTooltip = input<string>("Variant not available", {alias: "notAvailableTooltip"})
    readonly $useCaptions = input<boolean>(false, {alias: "useCaptions"})

    readonly $tooltipAppendTo = computed(() => this.$tooltipContainer() ?? cmTooltipAppendTo)

    readonly $loading = signal<boolean>(false)

    readonly configMenuService = inject(ConfigMenuService)

    readonly $isLightBackground = toSignal(
        toObservable(this.$variant).pipe(
            switchMap((variant) => {
                if (!variant) return of(false)
                if (variant.iconDataObjectId !== undefined) {
                    return this.configMenuService.isThumbnailLight$(variant.iconDataObjectId).pipe(map((v) => v ?? false))
                }
                const color = variant.iconColor
                if (!color) return of(false)
                const [r, g, b] = color
                return of(0.299 * r + 0.587 * g + 0.114 * b > 0.6)
            }),
        ),
        {initialValue: false},
    )

    onThumbnailLoaded(event: Event): void {
        const id = this.$variant()?.iconDataObjectId
        if (id == null) return
        this.configMenuService.sampleThumbnailLightness(id, event.target as HTMLImageElement)
    }
    readonly destroyRef = inject(DestroyRef)

    constructor() {
        this.configMenuService.synchronizing$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((synchronizing) => {
            if (!synchronizing) this.$loading.set(false)
        })

        this.configMenuService.configSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({config, variant}) => {
            const buttonConfig = this.$config()
            if (!buttonConfig) return
            const buttonVariant = this.$variant()
            if (!buttonVariant) return

            // Different config group: leave this button untouched (cross-group spinners may coexist).
            if (config.props.id !== buttonConfig.props.id) return
            // Same group: only the clicked variant spins; a sibling selection clears this button's stale spinner.
            this.$loading.set(variant.id === buttonVariant.id)
        })
    }

    getConfigVariantColor(): string {
        const variant = this.$variant()
        if (!variant) return "#898989"
        return this.configMenuService.configVariantColors[variant.id]
    }

    fetchThumbnailForDataObject(id?: number): Observable<string | null> {
        return this.configMenuService.fetchThumbnailForDataObject(id)
    }

    /**The CDK dragging tools did not work well here. They make the items themselves move and dragging outside of the parent
     * was problematic. So we use the native drag and drop API instead.
     *
     * Unwrapped ids are required, because a config group can be referenced from different templates, which changes the external id.
     * Since the group id is used to identify nodes in the pricing graph, only the internal / unwrapped id works.
     */
    onDragStart(event: DragEvent, variant: VariantInfo | undefined, config: ConfigInfo | undefined): void {
        if (!variant || !config) {
            return
        }
        const configuratorVariant: ConfiguratorVariant = {
            groupId: unwrapInterfaceId(config.props.id)[1],
            groupName: config.props.name,
            variantId: variant.id,
            variantName: variant.name,
        }
        if (event.dataTransfer) {
            event.dataTransfer.setData("text/plain", JSON.stringify(configuratorVariant))
        }
    }
}
